import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    ArrowRight,
    FileText,
    Loader2,
    Sparkles,
    UploadCloud,
    CheckCircle2,
    AlertCircle,
    Wand2,
    Edit3,
    X,
    ClipboardPaste,
    Lock,
    ExternalLink,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, createTender, createTenderFromText } from "@/lib/api";

const STEPS_AUTO = [
    { label: "Subiendo pliegos al almacenamiento seguro", icon: UploadCloud },
    { label: "Extrayendo texto de los documentos", icon: FileText },
    { label: "Claude Sonnet 4.5 analizando los pliegos", icon: Sparkles },
    { label: "Rellenando datos y generando resumen", icon: Wand2 },
];

export default function NewTender() {
    const navigate = useNavigate();
    const [mode, setMode] = useState("auto");

    return (
        <Layout>
            <div className="max-w-3xl mx-auto p-6 md:p-10" data-testid="new-tender-page">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors mb-6"
                    data-testid="back-btn"
                >
                    <ArrowLeft size={14} />
                    Volver
                </button>

                <div className="mb-8">
                    <div className="overline mb-2">Nuevo expediente</div>
                    <h1 className="font-display text-3xl md:text-4xl tracking-tight font-medium">
                        Crear nueva licitación
                    </h1>
                    <p className="text-sm md:text-base text-slate-500 mt-2 max-w-2xl">
                        Sube pliegos generales, técnicos, administrativos y cualquier otro
                        documento de interés. La IA los analizará como un único expediente,
                        extrayendo CPV, lotes, garantías y requisitos.
                    </p>
                    <div className="mt-3 flex items-start gap-2.5 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-3.5 max-w-2xl">
                        <Lock size={16} className="shrink-0 mt-0.5 text-amber-700" />
                        <div className="text-sm leading-relaxed">
                            <span className="font-bold">¿PDF protegido?</span> Si los pliegos
                            de la plataforma están protegidos o no se pueden descargar, puedes
                            desbloquearlos en{" "}
                            <a
                                href="https://docuconvert.es"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-bold underline hover:text-amber-700 inline-flex items-center gap-0.5"
                            >
                                docuconvert.es
                                <ExternalLink size={11} />
                            </a>{" "}
                            (gratis, online) y luego subirlos aquí. O también puedes pegar el
                            texto directamente con la opción "Pegar texto".
                        </div>
                    </div>
                </div>

                <div className="inline-flex bg-white border border-slate-200 p-1 rounded-xl mb-6">
                    <button
                        onClick={() => setMode("auto")}
                        className={`flex items-center gap-1.5 px-4 h-9 text-sm font-medium rounded-xl transition-colors ${
                            mode === "auto"
                                ? "bg-[#0055ff] text-white"
                                : "text-slate-600 hover:bg-slate-50"
                        }`}
                        data-testid="mode-auto"
                    >
                        <Sparkles size={14} />
                        Automático con IA
                    </button>
                    <button
                        onClick={() => setMode("paste")}
                        className={`flex items-center gap-1.5 px-4 h-9 text-sm font-medium rounded-xl transition-colors ${
                            mode === "paste"
                                ? "bg-[#0055ff] text-white"
                                : "text-slate-600 hover:bg-slate-50"
                        }`}
                        data-testid="mode-paste"
                    >
                        <ClipboardPaste size={14} />
                        Pegar texto
                    </button>
                    <button
                        onClick={() => setMode("manual")}
                        className={`flex items-center gap-1.5 px-4 h-9 text-sm font-medium rounded-xl transition-colors ${
                            mode === "manual"
                                ? "bg-[#0055ff] text-white"
                                : "text-slate-600 hover:bg-slate-50"
                        }`}
                        data-testid="mode-manual"
                    >
                        <Edit3 size={14} />
                        Manual
                    </button>
                </div>

                {mode === "auto" && <AutoMode navigate={navigate} />}
                {mode === "paste" && <PasteMode navigate={navigate} />}
                {mode === "manual" && <ManualMode navigate={navigate} />}
            </div>
        </Layout>
    );
}

/* ------------------------------ AUTO MODE ------------------------------ */
const AutoMode = ({ navigate }) => {
    const [dragging, setDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [stepIdx, setStepIdx] = useState(0);
    const [error, setError] = useState(null);
    const [files, setFiles] = useState([]);

    const addFiles = useCallback((list) => {
        const pdfs = Array.from(list || []).filter((f) => /\.pdf$/i.test(f.name));
        const nonPdfs = Array.from(list || []).length - pdfs.length;
        if (nonPdfs > 0) toast.warning(`${nonPdfs} archivo(s) ignorado(s): solo PDF`);
        if (!pdfs.length) return;
        setFiles((prev) => {
            const names = new Set(prev.map((f) => f.name));
            return [...prev, ...pdfs.filter((f) => !names.has(f.name))];
        });
    }, []);

    const removeFile = (name) =>
        setFiles((prev) => prev.filter((f) => f.name !== name));

    const launch = async () => {
        if (!files.length) {
            toast.error("Añade al menos un PDF");
            return;
        }
        setError(null);
        setProcessing(true);
        setStepIdx(0);
        const timers = [
            setTimeout(() => setStepIdx(1), 1200),
            setTimeout(() => setStepIdx(2), 2600),
            setTimeout(() => setStepIdx(3), 10000),
        ];
        try {
            const fd = new FormData();
            files.forEach((f) => fd.append("files", f));
            const { data: tender } = await api.post("/tenders/from-pdf", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 240000,
            });
            setStepIdx(STEPS_AUTO.length);
            toast.success("Licitación creada y analizada");
            timers.forEach(clearTimeout);
            setTimeout(() => navigate(`/app/licitacion/${tender.id}`), 500);
        } catch (e) {
            timers.forEach(clearTimeout);
            const msg = e?.response?.data?.detail || "No se pudo procesar el pliego";
            setError(msg);
            toast.error(msg);
            setProcessing(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        addFiles(e.dataTransfer.files);
    };

    if (processing) {
        return (
            <div
                className="bg-white border border-slate-200 p-8 md:p-10"
                data-testid="auto-processing"
            >
                <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 bg-[#0055ff] text-white flex items-center justify-center rounded-xl">
                        <Sparkles size={18} className="animate-pulse" />
                    </div>
                    <div className="min-w-0">
                        <div className="overline">Análisis en curso</div>
                        <div className="font-display text-lg font-medium truncate">
                            {files.length} documento{files.length > 1 ? "s" : ""}
                        </div>
                    </div>
                </div>
                <ol className="space-y-4">
                    {STEPS_AUTO.map((s, i) => {
                        const done = i < stepIdx;
                        const active = i === stepIdx;
                        return (
                            <li
                                key={s.label}
                                className={`flex items-center gap-3 p-3 border rounded-xl transition-colors ${
                                    done
                                        ? "border-emerald-200 bg-emerald-50"
                                        : active
                                          ? "border-[#0055ff] bg-sky-50/50"
                                          : "border-slate-200 bg-white opacity-60"
                                }`}
                            >
                                <div className="w-8 h-8 shrink-0 flex items-center justify-center rounded-xl">
                                    {done ? (
                                        <CheckCircle2 className="text-emerald-600" size={18} />
                                    ) : active ? (
                                        <Loader2 className="animate-spin text-[#0055ff]" size={18} />
                                    ) : (
                                        <s.icon className="text-slate-400" size={18} />
                                    )}
                                </div>
                                <div
                                    className={`text-sm ${
                                        done
                                            ? "text-emerald-800"
                                            : active
                                              ? "text-slate-900 font-medium"
                                              : "text-slate-500"
                                    }`}
                                >
                                    {s.label}
                                </div>
                            </li>
                        );
                    })}
                </ol>
                <div className="text-xs text-slate-500 mt-6 text-center">
                    El análisis completo suele tardar entre 20 y 90 segundos.
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <label
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                className={`block cursor-pointer border-2 border-dashed rounded-xl p-10 md:p-12 transition-all text-center ${
                    dragging
                        ? "border-[#0055ff] bg-sky-50/50"
                        : "border-slate-300 bg-white hover:border-[#0055ff]/60 hover:bg-slate-50/50"
                }`}
                data-testid="auto-pdf-dropzone"
            >
                <input
                    type="file"
                    accept="application/pdf,.pdf"
                    multiple
                    className="hidden"
                    onChange={(e) => addFiles(e.target.files)}
                    data-testid="auto-pdf-input"
                />
                <div className="flex flex-col items-center gap-4">
                    <div className="w-14 h-14 bg-[#0055ff] text-white flex items-center justify-center rounded-xl">
                        <UploadCloud size={24} />
                    </div>
                    <div>
                        <div className="font-display text-xl md:text-2xl font-medium mb-1">
                            Arrastra aquí los pliegos
                        </div>
                        <div className="text-sm text-slate-500">
                            PCAP, PPT o consolidado — puedes añadir varios PDFs
                        </div>
                    </div>
                    <div className="inline-flex items-center gap-2 text-xs text-[#0055ff] font-medium border border-[#0055ff]/20 bg-sky-50 px-3 py-1 rounded-xl mt-2">
                        <Sparkles size={12} />
                        Claude Sonnet 4.5 analizará todos juntos
                    </div>
                </div>
            </label>

            {/* File list */}
            {files.length > 0 && (
                <div className="bg-white border border-slate-200 divide-y divide-slate-100">
                    {files.map((f) => (
                        <div
                            key={f.name}
                            className="p-3 flex items-center justify-between gap-3"
                            data-testid={`staged-file-${f.name}`}
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-8 h-8 bg-[#0055ff]/5 text-[#0055ff] flex items-center justify-center rounded-xl shrink-0">
                                    <FileText size={14} />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-slate-900 truncate">
                                        {f.name}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        {(f.size / 1024).toFixed(0)} KB
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => removeFile(f.name)}
                                className="text-slate-400 hover:text-red-600 p-1.5"
                                data-testid={`remove-file-${f.name}`}
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {files.length > 0 && (
                <div className="flex justify-end">
                    <Button
                        onClick={launch}
                        size="lg"
                        className="rounded-xl bg-[#0055ff] hover:bg-[#0044cc] text-white"
                        data-testid="auto-launch-btn"
                    >
                        <Sparkles size={16} className="mr-2" />
                        Analizar {files.length} pliego{files.length > 1 ? "s" : ""} con IA
                    </Button>
                </div>
            )}

            {error && (
                <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 p-4 rounded-xl text-sm">
                    <AlertCircle size={16} className="mt-0.5 shrink-0" />
                    <div className="min-w-0">
                        <div className="font-medium mb-0.5">No se pudo procesar</div>
                        <div className="text-red-700">{error}</div>
                    </div>
                </div>
            )}

            <div className="grid md:grid-cols-3 gap-0 border border-slate-200 bg-white">
                {[
                    ["1", "Sube uno o varios PDF", "PCAP y PPT pueden ir por separado"],
                    ["2", "La IA lee y extrae", "Título, órgano, presupuesto, CPV, lotes, garantías"],
                    ["3", "Revisa y ajusta", "Abrimos la ficha con todo pre-rellenado"],
                ].map(([n, t, d], i) => (
                    <div
                        key={n}
                        className={`p-5 ${i < 2 ? "md:border-r border-slate-200" : ""} ${
                            i > 0 ? "border-t md:border-t-0 border-slate-200" : ""
                        }`}
                    >
                        <div className="font-mono text-xs text-[#0055ff] font-semibold mb-2">
                            PASO {n}
                        </div>
                        <div className="font-display font-medium mb-1">{t}</div>
                        <div className="text-xs text-slate-500 leading-relaxed">{d}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

/* ------------------------------ PASTE MODE ------------------------------ */
const PasteMode = ({ navigate }) => {
    const [text, setText] = useState("");
    const [title, setTitle] = useState("");
    const [processing, setProcessing] = useState(false);
    const [stepIdx, setStepIdx] = useState(0);
    const [error, setError] = useState(null);

    const STEPS = [
        { label: "Procesando texto pegado", icon: ClipboardPaste },
        { label: "Claude Sonnet 4.5 analizando", icon: Sparkles },
        { label: "Rellenando datos y resumen", icon: Wand2 },
    ];

    const launch = async () => {
        const trimmed = text.trim();
        if (trimmed.length < 200) {
            toast.error("Pega al menos 200 caracteres del pliego");
            return;
        }
        setError(null);
        setProcessing(true);
        setStepIdx(0);
        const timers = [
            setTimeout(() => setStepIdx(1), 800),
            setTimeout(() => setStepIdx(2), 6000),
        ];
        try {
            const tender = await createTenderFromText(trimmed, title);
            setStepIdx(STEPS.length);
            timers.forEach(clearTimeout);
            toast.success("Licitación creada y analizada");
            setTimeout(() => navigate(`/app/licitacion/${tender.id}`), 400);
        } catch (e) {
            timers.forEach(clearTimeout);
            const msg = e?.response?.data?.detail || "No se pudo procesar el texto";
            setError(msg);
            toast.error(msg);
            setProcessing(false);
        }
    };

    if (processing) {
        return (
            <div
                className="bg-white border border-slate-200 p-8 md:p-10 rounded-2xl shadow-pop"
                data-testid="paste-processing"
            >
                <div className="flex items-center gap-3 mb-8">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-600 to-cyan-500 text-white flex items-center justify-center shadow-pop">
                        <Sparkles size={20} className="animate-pulse" />
                    </div>
                    <div>
                        <div className="overline">Análisis en curso</div>
                        <div className="font-display text-lg font-bold">
                            {text.length.toLocaleString("es-ES")} caracteres
                        </div>
                    </div>
                </div>
                <ol className="space-y-3">
                    {STEPS.map((s, i) => {
                        const done = i < stepIdx;
                        const active = i === stepIdx;
                        return (
                            <li
                                key={s.label}
                                className={`flex items-center gap-3 p-3 border rounded-xl transition-colors ${
                                    done
                                        ? "border-emerald-200 bg-emerald-50"
                                        : active
                                          ? "border-brand-300 bg-brand-50"
                                          : "border-ink-100 bg-white opacity-60"
                                }`}
                            >
                                <div className="w-8 h-8 shrink-0 flex items-center justify-center rounded-xl">
                                    {done ? (
                                        <CheckCircle2 className="text-emerald-600" size={18} />
                                    ) : active ? (
                                        <Loader2 className="animate-spin text-brand-600" size={18} />
                                    ) : (
                                        <s.icon className="text-ink-400" size={18} />
                                    )}
                                </div>
                                <div
                                    className={`text-sm font-medium ${
                                        done
                                            ? "text-emerald-800"
                                            : active
                                              ? "text-ink-950 font-bold"
                                              : "text-ink-500"
                                    }`}
                                >
                                    {s.label}
                                </div>
                            </li>
                        );
                    })}
                </ol>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <div className="bg-brand-50 border border-brand-100 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-600 to-cyan-500 text-white flex items-center justify-center shrink-0">
                        <ClipboardPaste size={18} />
                    </div>
                    <div className="text-sm leading-relaxed text-ink-700 font-medium">
                        <div className="font-display font-bold text-base text-ink-950 mb-1">
                            ¿Cómo pegar el contenido?
                        </div>
                        Cuando el PDF del pliego no se pueda descargar, abre el documento, pulsa{" "}
                        <kbd className="font-mono text-xs bg-white border border-ink-200 px-1.5 py-0.5 rounded">
                            Ctrl + A
                        </kbd>{" "}
                        para seleccionar todo,{" "}
                        <kbd className="font-mono text-xs bg-white border border-ink-200 px-1.5 py-0.5 rounded">
                            Ctrl + C
                        </kbd>{" "}
                        para copiar, y aquí debajo pulsa{" "}
                        <kbd className="font-mono text-xs bg-white border border-ink-200 px-1.5 py-0.5 rounded">
                            Ctrl + V
                        </kbd>{" "}
                        para pegarlo. La IA hará el resto.
                    </div>
                </div>
            </div>

            <div className="bg-white border border-ink-100 rounded-2xl p-6 space-y-4 shadow-pop">
                <div>
                    <Label className="overline block mb-2">Título (opcional)</Label>
                    <Input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Ej. Servicio de mantenimiento informático Ayuntamiento X"
                        className="rounded-xl border-ink-100 h-11 font-medium"
                        data-testid="paste-title"
                    />
                </div>
                <div>
                    <Label className="overline block mb-2">
                        Pega aquí el contenido del pliego *
                    </Label>
                    <Textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Pega aquí (Ctrl + V) el texto completo del pliego — PCAP, PPT, anuncios, etc."
                        rows={14}
                        className="rounded-xl border-ink-100 font-mono text-xs leading-relaxed resize-y"
                        data-testid="paste-textarea"
                    />
                    <div className="flex items-center justify-between mt-2 text-xs text-ink-400 font-medium">
                        <span>
                            {text.length.toLocaleString("es-ES")} caracteres
                            {text.length < 200 && text.length > 0 && (
                                <span className="text-amber-600 ml-2 font-bold">
                                    (mínimo 200)
                                </span>
                            )}
                        </span>
                        {text.length > 0 && (
                            <button
                                onClick={() => setText("")}
                                className="text-ink-500 hover:text-red-600 transition-colors"
                                data-testid="paste-clear-btn"
                            >
                                Limpiar
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {error && (
                <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 p-4 rounded-xl text-sm">
                    <AlertCircle size={16} className="mt-0.5 shrink-0" />
                    <div className="min-w-0">
                        <div className="font-bold mb-0.5">No se pudo procesar</div>
                        <div className="text-red-700">{error}</div>
                    </div>
                </div>
            )}

            <div className="flex justify-end">
                <Button
                    onClick={launch}
                    size="lg"
                    disabled={text.trim().length < 200}
                    className="rounded-full bg-brand-600 hover:bg-brand-700 text-white px-7 h-12 font-bold shadow-pop hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="paste-launch-btn"
                >
                    <Sparkles size={16} className="mr-2" />
                    Analizar texto con IA
                </Button>
            </div>
        </div>
    );
};

/* ------------------------------ MANUAL MODE ------------------------------ */
const ManualMode = ({ navigate }) => {
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        title: "",
        reference: "",
        contracting_authority: "",
        deadline: "",
        budget: "",
        notes: "",
    });

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) {
            toast.error("Indica un título para la licitación");
            return;
        }
        setSubmitting(true);
        try {
            const body = {
                ...form,
                budget: form.budget ? Number(form.budget) : null,
                deadline: form.deadline || null,
            };
            const t = await createTender(body);
            toast.success("Licitación creada");
            navigate(`/app/licitacion/${t.id}`);
        } catch {
            toast.error("No se pudo crear la licitación");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={onSubmit} className="bg-white border border-slate-200 p-6 md:p-8 space-y-6">
            <Field label="Título *">
                <Input
                    value={form.title}
                    onChange={(e) => update("title", e.target.value)}
                    placeholder="Servicios de mantenimiento informático Ayuntamiento X"
                    className="rounded-xl border-slate-300"
                    data-testid="input-title"
                />
            </Field>
            <div className="grid md:grid-cols-2 gap-6">
                <Field label="Referencia / Expediente">
                    <Input
                        value={form.reference}
                        onChange={(e) => update("reference", e.target.value)}
                        placeholder="2026/01/001"
                        className="rounded-xl border-slate-300 font-mono"
                        data-testid="input-reference"
                    />
                </Field>
                <Field label="Órgano de contratación">
                    <Input
                        value={form.contracting_authority}
                        onChange={(e) => update("contracting_authority", e.target.value)}
                        placeholder="Ayuntamiento de Madrid"
                        className="rounded-xl border-slate-300"
                        data-testid="input-authority"
                    />
                </Field>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
                <Field label="Fecha límite de presentación">
                    <Input
                        type="date"
                        value={form.deadline}
                        onChange={(e) => update("deadline", e.target.value)}
                        className="rounded-xl border-slate-300"
                        data-testid="input-deadline"
                    />
                </Field>
                <Field label="Presupuesto base de licitación (€)">
                    <Input
                        type="number"
                        step="0.01"
                        value={form.budget}
                        onChange={(e) => update("budget", e.target.value)}
                        placeholder="120000"
                        className="rounded-xl border-slate-300 font-mono"
                        data-testid="input-budget"
                    />
                </Field>
            </div>
            <Field label="Notas internas">
                <Textarea
                    value={form.notes}
                    onChange={(e) => update("notes", e.target.value)}
                    placeholder="Observaciones, contactos clave, recordatorios…"
                    rows={4}
                    className="rounded-xl border-slate-300"
                    data-testid="input-notes"
                />
            </Field>

            <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-200">
                <Button
                    type="button"
                    variant="outline"
                    className="rounded-xl border-slate-300"
                    onClick={() => navigate("/app")}
                    data-testid="cancel-btn"
                >
                    Cancelar
                </Button>
                <Button
                    type="submit"
                    disabled={submitting}
                    className="rounded-xl bg-[#0055ff] hover:bg-[#0044cc] text-white"
                    data-testid="submit-tender-btn"
                >
                    {submitting ? (
                        "Creando…"
                    ) : (
                        <>
                            Crear licitación
                            <ArrowRight size={15} className="ml-1.5" />
                        </>
                    )}
                </Button>
            </div>
        </form>
    );
};

const Field = ({ label, children }) => (
    <div>
        <Label className="overline block mb-2">{label}</Label>
        {children}
    </div>
);
